import { datadogLogs } from '@datadog/browser-logs'
import { DateTime } from 'luxon'
import { useEffect, useRef } from 'react'

interface UseSessionTimeoutOptions {
  recordingStartedAt?: string | null
  serverTimestamp?: string | null
  isRecording: boolean
  userTimeLimit?: string | null
  isUserTimeLimitEnabled: boolean
  onTimeout: (reason: 'user-limit' | 'max-limit') => void
  logData?: Record<string, unknown>
}

const MAX_RECORDING_DURATION_MINUTES = 240 // 4 hours

export const useSessionTimeout = ({
  recordingStartedAt,
  serverTimestamp,
  isRecording,
  userTimeLimit,
  isUserTimeLimitEnabled,
  onTimeout,
  logData = {}
}: UseSessionTimeoutOptions) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!isRecording || !recordingStartedAt || !serverTimestamp) {
      if (!isRecording && timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      return
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    try {
      const startTime = DateTime.fromISO(recordingStartedAt)
      const currentServerTime = DateTime.fromISO(serverTimestamp)

      const elapsedMinutes = currentServerTime.diff(
        startTime,
        'minutes'
      ).minutes

      let timeLimitMinutes = MAX_RECORDING_DURATION_MINUTES
      let timeoutReason: 'user-limit' | 'max-limit' = 'max-limit'

      if (isUserTimeLimitEnabled && userTimeLimit) {
        const userLimitMinutes = parseInt(userTimeLimit, 10)
        if (userLimitMinutes < MAX_RECORDING_DURATION_MINUTES) {
          timeLimitMinutes = userLimitMinutes
          timeoutReason = 'user-limit'
        }
      }

      const remainingMinutes = timeLimitMinutes - elapsedMinutes

      if (remainingMinutes <= 0) {
        datadogLogs.logger.info('[Session Timeout] Session timeout triggered', {
          ...logData,
          elapsedMinutes,
          timeLimitMinutes,
          reason: timeoutReason
        })
        onTimeout(timeoutReason)
        return
      }

      const remainingMs = remainingMinutes * 60 * 1000

      timeoutRef.current = setTimeout(() => {
        datadogLogs.logger.info('[Session Timeout] Session timeout triggered', {
          ...logData,
          elapsedMinutes,
          timeLimitMinutes,
          reason: timeoutReason
        })
        onTimeout(timeoutReason)
      }, remainingMs)
    } catch (error) {
      datadogLogs.logger.error('[Session Timeout] Error setting timeout', {
        ...logData,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [
    recordingStartedAt,
    serverTimestamp,
    isRecording,
    userTimeLimit,
    isUserTimeLimitEnabled,
    onTimeout
  ])
}
